import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { IMAGES } from "../data/images";

const Philosophy = () => {
  const { t } = useLanguage();
  const [zoom, setZoom] = useState(null);
  const openZoom = (n) => {
    setZoom(n);
    window.__lenis?.stop();
  };
  const closeZoom = () => {
    setZoom(null);
    window.__lenis?.start();
  };
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const yB = useTransform(scrollYProgress, [0, 1], [80, -60]);

  return (
    <section id="chi-sono" data-testid="philosophy-section" className="relative py-24 sm:py-36 bg-travertine-light overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal>
          <Overline>{t.about.overline}</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 max-w-3xl">
            {t.about.title}
          </h2>
        </Reveal>

        <div className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-14 lg:gap-10">
          <div className="lg:col-span-7 space-y-20">
            {t.about.chapters.map((ch, i) => (
              <Reveal key={ch.num} delay={i * 0.12}>
                <div className="grid sm:grid-cols-12 gap-6">
                  <div className="sm:col-span-3">
                    <span className="font-serif italic text-6xl lg:text-7xl text-gold/70">{ch.num}.</span>
                    <div className="gold-hairline w-16 mt-4" />
                  </div>
                  <div className="sm:col-span-9">
                    <h3 className="font-serif text-2xl lg:text-3xl text-ink mb-5">{ch.heading}</h3>
                    <p data-testid={`philosophy-chapter-${i + 1}`} className="text-base lg:text-lg leading-relaxed text-ink/70">
                      {ch.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.1}>
              <div className="grid sm:grid-cols-12 gap-6">
                <div className="sm:col-span-3">
                  <span className="font-serif italic text-6xl lg:text-7xl text-gold/70">{t.about.quoteNum}.</span>
                  <div className="gold-hairline w-16 mt-4" />
                </div>
                <div className="sm:col-span-9">
                  <h3 className="font-serif text-2xl lg:text-3xl text-ink mb-5">{t.about.quoteHeading}</h3>
                  <blockquote
                    data-testid="philosophy-quote"
                    className="border-l-2 border-gold pl-8 py-2 font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink"
                  >
                    “{t.about.quote}”
                  </blockquote>
                </div>
              </div>
            </Reveal>
          </div>

          <div ref={imgRef} className="lg:col-span-5 relative hidden lg:block">
            <div className="sticky top-28 space-y-10">
              <motion.figure style={{ y: yA }} className="frame-gold overflow-hidden rounded-[28px] ml-12 transition-transform duration-500 hover:-translate-y-2">
                <img src={IMAGES.atelierFurnace} alt="Murano furnace" className="w-full aspect-[4/5] object-cover" />
                <figcaption className="absolute bottom-3 left-4 text-[10px] font-mono uppercase tracking-[0.3em] text-travertine-light/90 drop-shadow">
                  {t.about.imageCaptionA}
                </figcaption>
              </motion.figure>
              <motion.figure style={{ y: yB }} className="frame-gold overflow-hidden rounded-[28px] w-[85%] -mt-6 transition-transform duration-500 hover:-translate-y-2">
                <img src={IMAGES.atelierBench} alt="Feel 925 moodboard" className="w-full aspect-[4/3] object-cover" />
                <figcaption className="absolute bottom-3 left-4 text-[10px] font-mono uppercase tracking-[0.3em] text-travertine-light/90 drop-shadow">
                  {t.about.imageCaptionB}
                </figcaption>
              </motion.figure>
            </div>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-20 lg:mt-28">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold mb-8">
            {t.about.sketchesTitle}
          </p>
          <div data-testid="sketches-strip" className="space-y-5 w-screen ml-[calc(50%-50vw)] px-3 sm:px-5">
            {[[1, 2, 3, 4, 5], [6, 7, 8, 9]].map((row, r) => (
              <div key={r} className={`flex items-center ${r === 1 ? "pl-[7%]" : ""}`}>
                {row.map((n, i) => (
                  <div
                    key={n}
                    className="relative flex-1 min-w-0 hover:z-30"
                    style={{ transform: `rotate(${[-2, 1.5, -1.5, 2, -1][(i + r * 2) % 5]}deg)`, marginLeft: i === 0 ? 0 : -30 }}
                  >
                    <div
                      data-testid={`sketch-zoom-${n}`}
                      onClick={() => openZoom(n)}
                      className="cursor-pointer transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-4"
                    >
                      <img
                        src={`/images/sketch-2-${n}.jpg`}
                        alt={`${t.about.sketchesTitle} ${n}`}
                        loading="lazy"
                        data-testid={`sketch-card-${n}`}
                        className="w-full aspect-[3/2] object-cover rounded-[18px] frame-gold"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {zoom !== null && (
          <motion.div
            data-testid="sketch-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-10 bg-ink/75 backdrop-blur-sm"
            onClick={closeZoom}
          >
            <motion.img
              src={`/images/sketch-2-${zoom}.jpg`}
              alt={`${t.about.sketchesTitle} ${zoom}`}
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-[24px] frame-gold"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="sketch-lightbox-close"
              onClick={closeZoom}
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Philosophy;
